import type { FunctionComponent } from 'react'
import styled from 'styled-components'
import { StyledH4 } from '../../../stylesNew'
import Cookie from '../../../assets/icons/cookie.svg'
import { StyledChevron } from './CookieModal'
import AccordionBlock from '../../Accordion/AccordionBlock'
import Paragraph from '../../Paragraph/Paragraph'
import Link, { Variant } from '../../Links/Link/Link'

const StyledDiv = styled.div`
  background-color: var(--greyLight);
  padding: 1rem;
  margin-top: 1rem;
  p {
    margin: 0.5rem 0 0;
  }

  @media screen and (min-width: 48rem) {
    padding: 1rem 1.5rem;
  }
`

const StyledH6 = styled.h6`
  font-size: 1rem;
  @media screen and (min-width: 48rem) {
    font-size: 1.125rem;
  }
`

const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;

  p {
    margin: 0 0 0.5rem;
  }
`

const IconWrapper = styled.div`
  display: flex;
  height: 1.5rem;
  width: 2rem;
  padding-right: 0.5rem;
  svg {
    height: 1.5rem;
    width: 1.5rem;
  }
`

const BackButton = styled.button`
  border: none;
  background-color: var(--light);
  font-size: 1rem;
  display: flex;
  align-items: center;
  padding: 0;
  margin-bottom: 1.5rem;
  @media screen and (min-width: 48rem) {
    font-size: 1.125rem;
  }
`
const RowWrapper = styled.div`
  padding: 1rem 0 0;
  display: flex;
`

const TopContent = styled(Paragraph)`
  margin-bottom: 1.5rem;
  @media screen and (min-width: 48rem) {
    margin-bottom: 2.5rem;
  }
`

const AccordionBlockWrapper = styled.div`
  h5 {
    padding: 0;
  }
`

interface CookieOverviewProps {
  setShowOverview: (show: boolean) => void
}

const CookieOverview: FunctionComponent<CookieOverviewProps> = ({ setShowOverview }) => {
  return (
    <>
      <BackButton onClick={() => setShowOverview(false)}>
        <StyledChevron />
        Terug
      </BackButton>
      <StyledH4 forwardedAs="h4" id="dialog_label">
        Overzicht van cookies
      </StyledH4>
      <TopContent small>
        Hieronder volgt een overzicht van de cookies die wij willen gebruiken. U kunt de diensten
        die wij op deze site willen gebruiken en beoordelen. Voor meer informatie over hoe de
        gemeente Amsterdam uw persoonsgegevens verwerkt, kunt u onze{' '}
        <Link
          href="https://www.amsterdam.nl/privacy/"
          variant={Variant.Blue}
          target="_blank"
          rel="noreferrer"
        >
          Privacyverklaring
        </Link>{' '}
        lezen.
      </TopContent>

      <AccordionBlockWrapper>
        <AccordionBlock id="functionele" title="Gebruikte functionele cookies">
          <RowWrapper>
            <IconWrapper>
              <Cookie />
            </IconWrapper>
            <ColumnWrapper>
              <StyledH6>AUTH_SESSION_ID</StyledH6>
              <Paragraph small>
                Deze cookienaam is gekoppeld aan de identiteits- en toegangsbeheer en refereert naar
                de huidige authenticatiesessie voor ingelogde bezoekers.
              </Paragraph>
              <Paragraph small>
                <strong>Afloop:</strong> N.v.t.
              </Paragraph>
            </ColumnWrapper>
          </RowWrapper>
          <RowWrapper>
            <IconWrapper>
              <Cookie />
            </IconWrapper>
            <ColumnWrapper>
              <StyledH6>AUTH_SESSION_ID_LEGACY</StyledH6>
              <Paragraph small>
                Deze cookienaam is geassocieerd met de identiteits- en toegangsbeheer en refereert
                naar de huidige authenticatiesessie voor ingelogde bezoekers.
              </Paragraph>
              <Paragraph small>
                <strong>Afloop:</strong> N.v.t.
              </Paragraph>
            </ColumnWrapper>
          </RowWrapper>
        </AccordionBlock>
      </AccordionBlockWrapper>
      <AccordionBlockWrapper>
        <AccordionBlock id="analytische" title="Gebruikte analytische cookies">
          <StyledH6>
            <Link
              href="https://data.amsterdam.nl"
              variant={Variant.Blue}
              target="_blank"
              rel="noreferrer"
            >
              Data.amsterdam.nl
            </Link>
          </StyledH6>
          <RowWrapper>
            <IconWrapper>
              <Cookie />
            </IconWrapper>
            <ColumnWrapper>
              <StyledH6>ai_session</StyledH6>
              <Paragraph small>
                Deze cookienaam is geassocieerd met de Microsoft Application Insights-software. Wij
                gebruiken de ai_session als een unieke anonieme sessie-identificatiecookie om
                statistisch gebruik en telemetrie-informatie te verzamelen.
              </Paragraph>
              <Paragraph small>
                <strong>Afloop:</strong> 2 jaar
              </Paragraph>
            </ColumnWrapper>
          </RowWrapper>
          <RowWrapper>
            <IconWrapper>
              <Cookie />
            </IconWrapper>
            <ColumnWrapper>
              <StyledH6>ai_user</StyledH6>
              <Paragraph small>
                Deze cookienaam is gekoppeld aan de Microsoft Application Insights-software. Wij
                gebruiken deze cookie om statistisch gebruik en telemetrie-informatie te verzamelen.
                De ai_user is een unieke gebruikersidentificatiecookie waarmee het aantal gebruikers
                dat in de loop van de tijd gebruik maakt van de site wordt geteld.
              </Paragraph>
              <Paragraph small>
                <strong>Afloop:</strong> 364 dagen
              </Paragraph>
            </ColumnWrapper>
          </RowWrapper>
        </AccordionBlock>
      </AccordionBlockWrapper>
      <StyledDiv>
        <StyledH6>Cookies verwijderen</StyledH6>
        <Paragraph small>
          U kunt uw cookies verwijderen via de instelling van uw browser. Ook kunt u via de
          instelling van uw browser de plaatsing van cookies helemaal of ten dele uitsluiten. Meer
          informatie over cookies en het uitschakelen van cookies vindt u op{' '}
          <Link
            href="https://veiliginternetten.nl/"
            variant={Variant.Blue}
            target="_blank"
            rel="noreferrer"
          >
            veiliginternetten.nl
          </Link>
          . Voor professionals:{' '}
          <Link href="https://www.ncsc.nl/" variant={Variant.Blue} target="_blank" rel="noreferrer">
            Nationaal Cyber Security Centrum
          </Link>
          .
        </Paragraph>
      </StyledDiv>
    </>
  )
}

export default CookieOverview
